import { useMemo } from 'react';
import { useSiteSpeciesRiskQuery } from '../state/api/portfolioSiteExplorerGraphSlice';
import type { SiteSpeciesAssessmentItem } from '../state/api/porfolioSiteExplorerQuery';

/** One species at one site, as the modal renders it, whichever source supplied it. */
export type SiteSpeciesRiskItem = {
    scientificName: string;
    commonName: string;
    iucnRedListCategory: string;
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
    /**
     * First appeared at this site in the served snapshot (DD-6012).
     */
    isNew: boolean;
};

export type SiteSpeciesRiskResult = {
    siteName: string;
    country?: string;
    area?: number;
    redListSpecies: SiteSpeciesRiskItem[];
    invasiveSpecies: SiteSpeciesRiskItem[];
    isFetching: boolean;
};

type Args = {
    siteId: string;
    filters?: { name: string; values: string[] }[];
};

/**
 * The species risk modal's data. The grid is the only source since DD-5858;
 * normalising still happens here so the modal keeps its one shape.
 */
export const useSiteSpeciesRisk = ({ siteId, filters }: Args): SiteSpeciesRiskResult => {
    const args = { siteId, ...(filters?.length ? { filters } : {}) };
    const grid = useSiteSpeciesRiskQuery(args, { skip: !siteId });

    // Memoised: the arrays are rebuilt by map(), so without this every render hands
    // the modal fresh identities for unchanged data; its table-state effect keys on
    // them and would re-fire until React gives up with "Maximum update depth exceeded".
    return useMemo(
        () => ({
            siteName: grid.currentData?.siteName ?? '',
            country: grid.currentData?.country,
            area: grid.currentData?.area,
            redListSpecies: (grid.currentData?.redListSpecies ?? []).map(fromGrid),
            invasiveSpecies: (grid.currentData?.invasiveSpecies ?? []).map(fromGrid),
            isFetching: grid.isFetching,
        }),
        [grid.currentData, grid.isFetching]
    );
};

const fromGrid = (species: SiteSpeciesAssessmentItem): SiteSpeciesRiskItem => ({
    scientificName: species.scientificName,
    commonName: species.commonName ?? '',
    iucnRedListCategory: species.iucnRedListCategory,
    predictionLevel: species.predictionLevel,
    hasEDNAData: species.hasEDNAData,
    hasUploadedData: species.hasUploadedData,
    foundInEDNA: species.foundInEDNA,
    foundInUploadedData: species.foundInUploadedData,
    isNew: species.isNew ?? false,
});
